import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Instagram, Youtube, Twitter, Facebook, MessageCircle, Send, Smartphone, Mail,
  ImagePlus, Video, Sparkles, Calendar as CalendarIcon, Coins, Loader2, Check, X,
  Upload, FileImage, FileVideo, Trash2, RefreshCw, Brain, Camera
} from 'lucide-react';

const CHANNEL_ICONS = {
  instagram: Instagram, youtube: Youtube, twitter: Twitter, facebook: Facebook,
  whatsapp: MessageCircle, telegram: Send, sms: Smartphone, email: Mail
};
const CHANNEL_COLORS = {
  instagram: '#E1306C', youtube: '#FF0033', twitter: '#A8B3BD', facebook: '#1877F2',
  whatsapp: '#22C55E', telegram: '#2AABEE', sms: '#84CC16', email: '#F59E0B'
};
const CHANNEL_NAMES = {
  instagram: 'Instagram', youtube: 'YouTube', twitter: 'X (Twitter)', facebook: 'Facebook',
  whatsapp: 'WhatsApp', telegram: 'Telegram', sms: 'SMS', email: 'E-posta'
};

export default function PostCreator() {
  const { t } = useTranslation();
  const { api, user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [content, setContent] = useState('');
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [connectedChannels, setConnectedChannels] = useState({});
  const [mediaUrl, setMediaUrl] = useState(null);
  const [mediaBase64, setMediaBase64] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [mediaSource, setMediaSource] = useState('ai'); // 'ai' | 'upload'
  const [uploadedFile, setUploadedFile] = useState(null); // { name, size, type, mediaId }
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [imagePrompt, setImagePrompt] = useState('');
  const [videoText, setVideoText] = useState('');
  const [generatingImage, setGeneratingImage] = useState(false);
  const [generatingVideo, setGeneratingVideo] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [describingMedia, setDescribingMedia] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [costEstimate, setCostEstimate] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // Pick up content from AI Studio
  useEffect(() => {
    const aiContent = sessionStorage.getItem('ai_content');
    const aiImage = sessionStorage.getItem('ai_image');
    if (aiContent) { setContent(aiContent); sessionStorage.removeItem('ai_content'); }
    if (aiImage) { setMediaBase64(aiImage); setMediaType('image'); setMediaSource('ai'); sessionStorage.removeItem('ai_image'); }
  }, []);

  useEffect(() => {
    api.get('/api/channels/status')
      .then(res => setConnectedChannels(res.data))
      .catch(() => {});
  }, [api]);

  useEffect(() => {
    if (selectedChannels.length > 0) {
      let total = 0;
      selectedChannels.forEach(ch => {
        if (['sms', 'whatsapp'].includes(ch)) total += 2;
        else if (ch === 'email') total += 1;
        else if (mediaUrl || mediaBase64) total += 2;
        else total += 1;
      });
      setCostEstimate({ total });
    } else {
      setCostEstimate(null);
    }
  }, [selectedChannels, mediaUrl, mediaBase64, mediaType]);

  const toggleChannel = (ch) => {
    setSelectedChannels(prev => prev.includes(ch) ? prev.filter(c => c !== ch) : [...prev, ch]);
  };

  // File upload handler
  const handleFileUpload = useCallback(async (file) => {
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'video/mp4', 'video/quicktime', 'video/webm'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Desteklenmeyen dosya türü. JPG, PNG, WebP, GIF, MP4 kabul edilir.');
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      toast.error('Dosya boyutu 100 MB\'ı aşamaz.');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/api/media/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadedFile({
        name: res.data.filename,
        size: res.data.file_size,
        type: res.data.content_type,
        mediaId: res.data.media_id,
        mediaType: res.data.media_type,
      });
      setMediaUrl(res.data.media_url);
      setMediaBase64(null);
      setMediaType(res.data.media_type);
      toast.success(t('common.success'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, [api, t]);

  // Drag & Drop handlers
  const handleDragOver = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); }, []);
  const handleDragLeave = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); }, []);
  const handleDrop = useCallback((e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragOver(false);
    const files = e.dataTransfer?.files;
    if (files?.length > 0) handleFileUpload(files[0]);
  }, [handleFileUpload]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file);
  };

  const removeMedia = () => {
    setMediaUrl(null);
    setMediaBase64(null);
    setMediaType(null);
    setUploadedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // AI describe uploaded media
  const handleDescribeMedia = async () => {
    if (!uploadedFile?.mediaId || selectedChannels.length === 0) {
      toast.error(t('post.noChannels'));
      return;
    }
    setDescribingMedia(true);
    try {
      const res = await api.post(`/api/ai/describe-media?media_id=${uploadedFile.mediaId}&content_type=${uploadedFile.mediaType}&channels=${selectedChannels.join(',')}&language=${t('common.loading').startsWith('Y') ? 'tr' : 'en'}`);
      
      // Use general caption or first channel's text
      if (res.data.general_caption) {
        setContent(res.data.general_caption);
      } else if (res.data.channels) {
        const firstChannel = Object.values(res.data.channels)[0];
        if (firstChannel?.text) {
          const hashtags = firstChannel.hashtags ? '\n\n' + firstChannel.hashtags.join(' ') : '';
          setContent(firstChannel.text + hashtags);
        }
      }
      refreshUser();
      toast.success(`AI metin üretildi! ${res.data.credits_used} kredi kullanıldı`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI description failed');
    } finally {
      setDescribingMedia(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!imagePrompt.trim()) return;
    setGeneratingImage(true);
    try {
      const res = await api.post('/api/media/image/dalle', { prompt: imagePrompt });
      setMediaBase64(res.data.image_base64);
      setMediaType('image');
      setMediaUrl(null);
      setUploadedFile(null);
      toast.success(t('common.success'));
      refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Image generation failed');
    } finally {
      setGeneratingImage(false);
    }
  };

  const handleGenerateVideo = async () => {
    if (!videoText.trim()) return;
    setGeneratingVideo(true);
    try {
      const res = await api.post('/api/media/video/heygen', { text: videoText });
      toast.success(`Video ${t('common.loading')} - ID: ${res.data.video_id}`);
      refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Video generation failed');
    } finally {
      setGeneratingVideo(false);
    }
  };

  const handlePublish = async () => {
    if (!content.trim()) { toast.error(t('post.contentRequired')); return; }
    if (selectedChannels.length === 0) { toast.error(t('post.noChannels')); return; }

    setPublishing(true);
    try {
      let postMediaUrl = mediaUrl;
      if (mediaBase64 && !mediaUrl) postMediaUrl = `data:image/png;base64,${mediaBase64}`;

      const postData = { content, channels: selectedChannels, media_url: postMediaUrl, media_type: mediaType };
      if (isScheduled && scheduledDate && scheduledTime) {
        postData.scheduled_at = `${scheduledDate}T${scheduledTime}:00Z`;
      }

      const createRes = await api.post('/api/posts', postData);
      const postId = createRes.data.id;

      if (!isScheduled) {
        const pubRes = await api.post(`/api/posts/${postId}/publish`);
        if (pubRes.data.success) {
          const ok = pubRes.data.results.filter(r => r.success).map(r => r.channel);
          const fail = pubRes.data.results.filter(r => !r.success);
          toast.success(`${t('post.publishedTo')}${ok.join(', ')}. ${t('post.creditsUsed')}${pubRes.data.credits_used}`);
          fail.forEach(f => toast.error(`${f.channel}: ${f.error}`, { duration: 8000 }));
        } else {
          pubRes.data.results.forEach(r => toast.error(`${r.channel}: ${r.error}`, { duration: 8000 }));
        }
      } else {
        toast.success(t('common.success') + ' - ' + t('post.scheduleLater'));
      }
      refreshUser();
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Publishing failed');
    } finally {
      setPublishing(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!content.trim()) { toast.error(t('post.contentRequired')); return; }
    try {
      let postMediaUrl = mediaUrl;
      if (mediaBase64 && !mediaUrl) postMediaUrl = `data:image/png;base64,${mediaBase64}`;
      await api.post('/api/posts', { content, channels: selectedChannels, media_url: postMediaUrl, media_type: mediaType });
      toast.success(t('common.success'));
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const hasMedia = !!(mediaUrl || mediaBase64);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
        {t('post.createTitle')}
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Editor */}
        <div className="lg:col-span-2 space-y-4">
          {/* Content */}
          <Card className="rounded-xl border border-white/10 bg-[hsl(var(--card))]/80">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">{t('post.content')}</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={content}
                onChange={e => setContent(e.target.value)}
                placeholder={t('post.contentPlaceholder')}
                className="min-h-[180px] bg-black/20 border-white/10 resize-none"
                data-testid="post-content-textarea"
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-[hsl(var(--muted-foreground))]">{content.length} {t('common.chars')}</span>
              </div>
            </CardContent>
          </Card>

          {/* Media Section */}
          <Card className="rounded-xl border border-white/10 bg-[hsl(var(--card))]/80">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Sparkles size={16} className="text-[hsl(var(--primary))]" />
                {t('post.media')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Media Source Selector - Segmented Control */}
              <div className="grid grid-cols-2 gap-2 p-1 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                <button
                  onClick={() => setMediaSource('ai')}
                  data-testid="media-source-ai"
                  className={`flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                    mediaSource === 'ai'
                      ? 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/20 shadow-[0_0_16px_-4px_rgba(212,175,55,0.2)]'
                      : 'text-[hsl(var(--muted-foreground))] hover:text-white hover:bg-white/[0.04]'
                  }`}
                >
                  <Sparkles size={16} />
                  <span>{t('post.aiGenerate')}</span>
                </button>
                <button
                  onClick={() => setMediaSource('upload')}
                  data-testid="media-source-upload"
                  className={`flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                    mediaSource === 'upload'
                      ? 'bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))] border border-[hsl(var(--accent))]/20 shadow-[0_0_16px_-4px_rgba(0,184,169,0.2)]'
                      : 'text-[hsl(var(--muted-foreground))] hover:text-white hover:bg-white/[0.04]'
                  }`}
                >
                  <Upload size={16} />
                  <span>{t('common.loading').startsWith('Y') ? 'Dosya Yükle' : 'Upload File'}</span>
                </button>
              </div>

              {/* AI Generate Mode */}
              {mediaSource === 'ai' && (
                <Tabs defaultValue="image">
                  <TabsList className="bg-[hsl(var(--secondary))]">
                    <TabsTrigger value="image" className="text-xs">
                      <ImagePlus size={14} className="mr-1" />
                      {t('post.aiGenerate')}
                    </TabsTrigger>
                    <TabsTrigger value="video" className="text-xs">
                      <Video size={14} className="mr-1" />
                      {t('post.videoGenerate')}
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="image" className="space-y-3">
                    <div className="flex gap-2">
                      <Input
                        value={imagePrompt}
                        onChange={e => setImagePrompt(e.target.value)}
                        placeholder={t('post.promptPlaceholder')}
                        className="bg-black/20 border-white/10"
                        data-testid="image-prompt-input"
                      />
                      <Button onClick={handleGenerateImage} disabled={generatingImage || !imagePrompt.trim()}
                        className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] whitespace-nowrap"
                        data-testid="generate-image-btn">
                        {generatingImage ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                        <span className="ml-1">{generatingImage ? t('post.generating') : t('post.generate')}</span>
                      </Button>
                    </div>
                  </TabsContent>

                  <TabsContent value="video" className="space-y-3">
                    <Textarea value={videoText} onChange={e => setVideoText(e.target.value)}
                      placeholder="Video script or narration text..."
                      className="bg-black/20 border-white/10 min-h-[80px]" data-testid="video-text-input" />
                    <Button onClick={handleGenerateVideo} disabled={generatingVideo || !videoText.trim()}
                      className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]" data-testid="generate-video-btn">
                      {generatingVideo ? <Loader2 size={16} className="animate-spin mr-2" /> : <Video size={16} className="mr-2" />}
                      {generatingVideo ? t('post.generating') : t('post.generate')}
                    </Button>
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">* {t('post.heygenNote')}</p>
                  </TabsContent>
                </Tabs>
              )}

              {/* Upload Mode */}
              {mediaSource === 'upload' && !uploadedFile && (
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="upload-dropzone"
                  className={`relative cursor-pointer rounded-2xl border-2 border-dashed transition-all duration-300 ${
                    isDragOver
                      ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/5 shadow-[0_0_30px_-8px_rgba(0,184,169,0.3)]'
                      : uploading
                        ? 'border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5'
                        : 'border-white/[0.08] hover:border-[hsl(var(--primary))]/30 hover:bg-white/[0.02]'
                  }`}
                >
                  <div className="flex flex-col items-center justify-center py-10 px-6">
                    {uploading ? (
                      <>
                        <Loader2 size={32} className="text-[hsl(var(--primary))] animate-spin mb-4" />
                        <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('common.loading')}</p>
                      </>
                    ) : (
                      <>
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[hsl(var(--primary))]/10 to-[hsl(var(--accent))]/10 flex items-center justify-center mb-4 border border-white/[0.06]">
                          <Upload size={24} className="text-[hsl(var(--primary))]" />
                        </div>
                        <p className="text-sm text-white font-medium mb-1">
                          {t('common.loading').startsWith('Y')
                            ? 'Fotoğraf veya video sürükle ya da dokunarak seç'
                            : 'Drag & drop photo or video, or tap to select'}
                        </p>
                        <p className="text-[11px] text-[hsl(var(--muted-foreground))]">
                          JPG, PNG, WebP, GIF, MP4 · Max 100 MB
                        </p>
                      </>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="file-input"
                    capture={undefined}
                  />
                </div>
              )}

              {/* Preview - Uploaded File */}
              {mediaSource === 'upload' && uploadedFile && (
                <div className="space-y-3">
                  {/* File preview */}
                  <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-black/30">
                    {uploadedFile.mediaType === 'image' && mediaUrl && (
                      <img src={mediaUrl} alt={uploadedFile.name}
                        className="w-full max-h-72 object-contain" data-testid="uploaded-preview" />
                    )}
                    {uploadedFile.mediaType === 'video' && mediaUrl && (
                      <video src={mediaUrl} controls className="w-full max-h-72" data-testid="uploaded-video-preview" />
                    )}
                  </div>

                  {/* File info card */}
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                    {uploadedFile.mediaType === 'image'
                      ? <FileImage size={18} className="text-[hsl(var(--primary))] shrink-0" />
                      : <FileVideo size={18} className="text-[hsl(var(--accent))] shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{uploadedFile.name}</p>
                      <p className="text-[11px] text-[hsl(var(--muted-foreground))]">
                        {formatFileSize(uploadedFile.size)} · {uploadedFile.type}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" className="h-8 text-xs gap-1"
                        onClick={() => fileInputRef.current?.click()}>
                        <RefreshCw size={12} />
                        {t('common.loading').startsWith('Y') ? 'Değiştir' : 'Replace'}
                      </Button>
                      <Button variant="ghost" size="sm" className="h-8 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 gap-1"
                        onClick={removeMedia} data-testid="remove-media-btn">
                        <Trash2 size={12} />
                        {t('common.loading').startsWith('Y') ? 'Kaldır' : 'Remove'}
                      </Button>
                    </div>
                    {/* Hidden file input for replace */}
                    <input ref={fileInputRef} type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm"
                      onChange={handleFileSelect} className="hidden" />
                  </div>

                  {/* AI Describe button */}
                  <Button
                    onClick={handleDescribeMedia}
                    disabled={describingMedia || selectedChannels.length === 0}
                    className="w-full bg-gradient-to-r from-[hsl(var(--primary))]/80 to-[hsl(var(--accent))]/80 text-white hover:from-[hsl(var(--primary))] hover:to-[hsl(var(--accent))] gap-2"
                    data-testid="ai-describe-btn"
                  >
                    {describingMedia
                      ? <Loader2 size={16} className="animate-spin" />
                      : <Brain size={16} />}
                    {describingMedia
                      ? (t('common.loading').startsWith('Y') ? 'AI metin üretiyor...' : 'AI generating text...')
                      : (t('common.loading').startsWith('Y')
                          ? `Bu ${uploadedFile.mediaType === 'image' ? 'görsele' : 'videoya'} göre AI ile metin üret`
                          : `Generate AI text for this ${uploadedFile.mediaType}`)}
                  </Button>
                  <p className="text-[11px] text-[hsl(var(--muted-foreground))] text-center">
                    {t('common.loading').startsWith('Y')
                      ? 'Dosya yükleme ücretsiz · AI metin üretimi kredi düşürür'
                      : 'File upload is free · AI text generation uses credits'}
                  </p>
                </div>
              )}

              {/* AI Generated Image Preview */}
              {mediaSource === 'ai' && mediaBase64 && (
                <div className="relative rounded-2xl overflow-hidden border border-white/10">
                  <img src={`data:image/png;base64,${mediaBase64}`} alt="Generated"
                    className="w-full max-h-72 object-contain bg-black/40" data-testid="generated-image-preview" />
                  <Button variant="ghost" size="icon"
                    className="absolute top-2 right-2 bg-black/50 hover:bg-black/70 rounded-xl"
                    onClick={removeMedia}>
                    <X size={16} />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right: Channels & Settings */}
        <div className="space-y-4">
          {/* Channel Selection */}
          <Card className="rounded-xl border border-white/10 bg-[hsl(var(--card))]/80">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">{t('post.channels')}</CardTitle>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">{t('post.channelsSelect')}</p>
            </CardHeader>
            <CardContent className="space-y-2">
              {Object.keys(CHANNEL_ICONS).map(ch => {
                const Icon = CHANNEL_ICONS[ch];
                const isConnected = connectedChannels[ch]?.connected;
                const isSelected = selectedChannels.includes(ch);
                return (
                  <button key={ch} onClick={() => isConnected && toggleChannel(ch)} disabled={!isConnected}
                    data-testid={`channel-select-${ch}`}
                    className={`flex items-center gap-3 w-full p-3 rounded-lg border-l-2 transition-colors duration-200 ${
                      isSelected ? 'bg-white/10 border-opacity-100'
                        : isConnected ? 'bg-white/5 hover:bg-white/8 border-opacity-50'
                        : 'opacity-30 cursor-not-allowed border-opacity-20'
                    }`} style={{ borderLeftColor: CHANNEL_COLORS[ch] }}>
                    <Icon size={18} style={{ color: CHANNEL_COLORS[ch] }} />
                    <span className="text-sm flex-1 text-left">{CHANNEL_NAMES[ch]}</span>
                    {isConnected ? (
                      isSelected ? <Check size={16} className="text-emerald-400" /> : <div className="w-4 h-4 rounded border border-white/20" />
                    ) : (
                      <Badge variant="outline" className="text-[10px] border-white/10">{t('settings.notConnected')}</Badge>
                    )}
                  </button>
                );
              })}
            </CardContent>
          </Card>

          {/* Schedule */}
          <Card className="rounded-xl border border-white/10 bg-[hsl(var(--card))]/80">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center justify-between">
                <span>{t('post.schedule')}</span>
                <Switch checked={isScheduled} onCheckedChange={setIsScheduled} data-testid="schedule-toggle" />
              </CardTitle>
            </CardHeader>
            {isScheduled && (
              <CardContent className="space-y-3">
                <div>
                  <label className="text-xs text-[hsl(var(--muted-foreground))] mb-1 block">{t('post.scheduleDate')}</label>
                  <div className="flex gap-2">
                    <Input type="date" value={scheduledDate} onChange={e => setScheduledDate(e.target.value)}
                      className="bg-black/20 border-white/10" data-testid="schedule-date" />
                    <Input type="time" value={scheduledTime} onChange={e => setScheduledTime(e.target.value)}
                      className="bg-black/20 border-white/10" data-testid="schedule-time" />
                  </div>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Cost Estimate */}
          {costEstimate && (
            <Card className="rounded-xl border border-[hsl(var(--primary))]/20 bg-[hsl(var(--primary))]/5">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm">{t('post.cost')}</span>
                  <div className="flex items-center gap-1">
                    <Coins size={16} className="text-[hsl(var(--primary))]" />
                    <span className="text-lg font-semibold tabular-nums" data-testid="cost-estimate">{costEstimate.total}</span>
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">{t('post.costCredits')}</span>
                  </div>
                </div>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                  {t('dashboard.credits')}: {user?.credits || 0}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Action Buttons */}
          <div className="space-y-2">
            <Button onClick={handlePublish} disabled={publishing || !content.trim()}
              className="w-full gold-sheen bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-105"
              data-testid="publish-btn">
              {publishing ? <Loader2 size={16} className="animate-spin mr-2" /> : <Check size={16} className="mr-2" />}
              {isScheduled ? t('post.scheduleLater') : t('post.publish')}
            </Button>
            <Button variant="outline" onClick={handleSaveDraft} className="w-full border-white/10" data-testid="save-draft-btn">
              {t('post.saveDraft')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
